import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Mail, MailOpen, Clock, Users, TrendingUp, AlertTriangle,
  BarChart3, Eye, Send, ExternalLink, Loader2,
} from 'lucide-react';
import api from '../lib/api';
import { formatDate, formatDateTime } from '../lib/utils';

export const EmailAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await api.get('/email-analytics');
        setData(res.data);
      } catch (err) {
        console.error('Failed to fetch email analytics:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  const overall = data?.overall || { total_sent: 0, total_opened: 0, open_rate: 0, avg_time_to_open: null };
  const byTemplate = data?.by_template || [];
  const unengaged = data?.unengaged_customers || [];

  return (
    <Layout>
      <div className="space-y-6" data-testid="email-analytics-page">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">Email Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">Track email engagement, open rates, and identify customers needing follow-up.</p>
        </div>

        {/* Overall Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="overall-stats">
          <Card className="border-slate-200">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                  <Send className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Emails Sent</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="total-sent">{overall.total_sent}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                  <MailOpen className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Emails Opened</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="total-opened">{overall.total_opened}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Open Rate</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="open-rate">{overall.open_rate}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Avg. Time to Open</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="avg-time">{overall.avg_time_to_open || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Open Rates by Template */}
          <div className="lg:col-span-2">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-500" />
                  Open Rates by Template
                </CardTitle>
                <CardDescription>Email performance breakdown by template type</CardDescription>
              </CardHeader>
              <CardContent>
                {byTemplate.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Mail className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p>No email tracking data yet.</p>
                    <p className="text-xs mt-1">Send emails with tracking to see analytics here.</p>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="template-rates">
                    {byTemplate.map((t, i) => (
                      <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{t.template}</p>
                          <p className="text-xs text-slate-500">{t.sent} sent, {t.opened} opened</p>
                        </div>
                        {/* Visual bar */}
                        <div className="w-32 hidden sm:block">
                          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${Math.min(t.rate, 100)}%`,
                                backgroundColor: t.rate >= 60 ? '#10b981' : t.rate >= 30 ? '#f59e0b' : '#ef4444',
                              }}
                            />
                          </div>
                        </div>
                        <div className="text-right shrink-0 w-16">
                          <span className={`text-sm font-bold ${t.rate >= 60 ? 'text-emerald-600' : t.rate >= 30 ? 'text-amber-600' : 'text-red-500'}`}>
                            {t.rate}%
                          </span>
                        </div>
                        {t.avg_time_to_open && (
                          <div className="text-right shrink-0 w-20 hidden md:block">
                            <p className="text-xs text-slate-400">Avg open</p>
                            <p className="text-xs font-medium text-slate-600">{t.avg_time_to_open}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Unengaged Customers */}
          <div className="lg:col-span-1">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Needs Follow-Up
                </CardTitle>
                <CardDescription>Customers who haven't opened recent emails (last 30 days)</CardDescription>
              </CardHeader>
              <CardContent>
                {unengaged.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Eye className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">All customers are engaged!</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="unengaged-list">
                    {unengaged.map((c, i) => (
                      <Link
                        key={i}
                        to={`/customers/${c.customer_id}`}
                        className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 hover:border-amber-200 hover:bg-amber-50/50 transition-colors group"
                        data-testid={`unengaged-customer-${i}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                          <Mail className="w-4 h-4 text-amber-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-slate-800 truncate">{c.name}</p>
                            <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-amber-500 shrink-0" />
                          </div>
                          <p className="text-xs text-slate-500 truncate">{c.email}</p>
                          <p className="text-xs text-slate-400 mt-1 truncate">
                            Last: {c.last_email_subject}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className="bg-red-100 text-red-600 border-red-200 text-xs">
                              {c.unopened_count} unopened
                            </Badge>
                            {c.last_email_sent && (
                              <span className="text-xs text-slate-400">{formatDate(c.last_email_sent)}</span>
                            )}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};
